import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11, default as _styled12 } from "styled-components";
import React from "react";
import { Grid } from "~/features/ui/grid";
import { type Mode } from "~/features/ui/get-theme-style";
import { CtaButtonPair, type CtaPairLink } from "./cta-button-pair";
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
export type IntroHeadlineBBlockViewProps = {
  anchor?: {
    id?: string | null;
    name?: string | null;
  } | null;
  eyebrow?: string | null;
  hasTopDivider?: boolean | null;
  headline?: string | null;
  headlineAccent?: string | null;
  mediaDesktop?: React.ReactNode;
  mediaMobile?: React.ReactNode;
  mediaPosition?: string | null;
  mode?: string | null;
  primaryLink?: CtaPairLink | null;
  secondaryLink?: CtaPairLink | null;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  text?: React.ReactNode;
  theme?: string | null;
};

/**
 * UI 3.0 "Intro Headline" module. A two-column layout (media + copy) with a
 * two-tone headline, an optional eyebrow, the solid/outline CTA button pair
 * directly under the headline, and supporting copy pinned to the bottom of the
 * column. Light/dark is driven by the section's `data-theme`.
 */
export function IntroHeadlineBBlockView({
  anchor,
  eyebrow,
  hasTopDivider = true,
  headline,
  headlineAccent,
  mediaDesktop,
  mediaMobile,
  mediaPosition = "left",
  mode = "light",
  primaryLink,
  secondaryLink,
  sectionProps,
  text,
  theme = "white"
}: IntroHeadlineBBlockViewProps) {
  const textCol = <_StyledDiv key="text-col" $_css={[{
    "gridColumn": "1 / -1",
    "display": "flex",
    "height": "100%",
    "flexDirection": "column",
    "alignItems": "flex-start",
    "gap": "var(--spacing-xl)",
    "@media (min-width: 1024px)": {
      "gridColumn": "span 6 / span 6",
      "paddingLeft": "var(--spacing-3xl)",
      "paddingRight": "var(--spacing-3xl)"
    }
  }]}>
      <_StyledDiv2 $_css2={[{
      "display": "block",
      "width": "100%",
      "@media (min-width: 1024px)": {
        "display": "none"
      }
    }]}>{mediaMobile}</_StyledDiv2>

      <_StyledDiv3 $_css3={[{
      "display": "flex",
      "width": "100%",
      "flex": "1 1 0%",
      "flexDirection": "column",
      "alignItems": "flex-start",
      "justifyContent": "space-between",
      "gap": "var(--spacing-xl)"
    }]}>
        <_StyledDiv4>
          {eyebrow && <_StyledSpan $_css4={[{
          "fontSize": "1.4rem",
          "lineHeight": "1.1",
          "letterSpacing": "0.028000000000000004rem",
          "fontWeight": "400",
          "textTransform": "uppercase",
          "color": "var(--text-secondary)",
          "@media (min-width: 1024px)": {
            "fontSize": "1.6rem",
            "lineHeight": "1.1",
            "letterSpacing": "0.016rem",
            "fontWeight": "400"
          }
        }]}>
              {eyebrow}
            </_StyledSpan>}

          {headline && <_StyledH $_css5={[{
          "fontSize": "2.4rem",
          "lineHeight": "1.2",
          "fontWeight": "400",
          "color": "var(--text-primary)",
          "@media (min-width: 1024px)": {
            "fontSize": "3.8rem",
            "lineHeight": "1.3",
            "letterSpacing": "0.038rem",
            "fontWeight": "400"
          }
        }]}>
              {headline}
              {headlineAccent && <_StyledSpan2>{` ${headlineAccent}`}</_StyledSpan2>}
            </_StyledH>}

          <CtaButtonPair primary={primaryLink} secondary={secondaryLink} />
        </_StyledDiv4>

        {text && <_StyledDiv5 $_css6={[{
        "fontSize": "1.6rem",
        "lineHeight": "1.5",
        "fontWeight": "400",
        "color": "var(--text-primary)",
        "@media (min-width: 1024px)": {
          "fontSize": "1.8rem",
          "lineHeight": "1.5",
          "letterSpacing": "0.018rem",
          "fontWeight": "400"
        }
      }]}>{text}</_StyledDiv5>}
      </_StyledDiv3>
    </_StyledDiv>;
  const mediaCol = <_StyledDiv6 key="media-col" $_css7={[{
    "gridColumn": "span 6 / span 6",
    "display": "none",
    "@media (min-width: 1024px)": {
      "display": "block"
    }
  }]}>
      <_StyledDiv7>{mediaDesktop}</_StyledDiv7>
    </_StyledDiv6>;
  const columns = mediaPosition === "right" ? [textCol, mediaCol] : [mediaCol, textCol];
  return <_StyledSection {...sectionProps} data-theme={mode as Mode} id={anchor?.id || undefined} data-anchor-name={anchor?.name || undefined} $_css8={[
  // `relative` makes the opaque section background cover the page
  // background pattern, which otherwise paints above static sections.
  {
    "position": "relative",
    "paddingLeft": "var(--spacing-lg)",
    "paddingRight": "var(--spacing-lg)",
    "paddingTop": "var(--spacing-3xl)",
    "paddingBottom": "var(--spacing-3xl)",
    "@media (min-width: 1024px)": {
      "paddingLeft": "var(--spacing-3xl)",
      "paddingRight": "var(--spacing-3xl)"
    }
  }, hasTopDivider && {
    "borderTopWidth": "1px",
    "borderColor": "var(--stroke-primary)"
  }, theme === "grey" ? {
    "backgroundColor": "var(--bg-tertiary)"
  } : {
    "backgroundColor": "var(--bg-primary)"
  }]}>
      <_StyledGrid $_css9={[{
      "@media (min-width: 1024px)": {
        "minHeight": "650px"
      }
    }]}>{columns}</_StyledGrid>
    </_StyledSection>;
}
var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  componentId: "sc-sf-1rkjxse-0"
})`${p => p.$_css}`;
var _StyledDiv2 = _styled2("div").withConfig({
  componentId: "sc-sf-1rkjxse-1"
})`${p => p.$_css2}`;
var _StyledDiv3 = _styled3("div").withConfig({
  componentId: "sc-sf-1rkjxse-2"
})`${p => p.$_css3}`;
var _StyledDiv4 = _styled4("div").withConfig({
  componentId: "sc-sf-1rkjxse-3"
})({
  "position": "relative",
  "zIndex": "10",
  "display": "flex",
  "flexDirection": "column",
  "alignItems": "flex-start",
  "gap": "var(--spacing-lg)"
});
var _StyledSpan = _styled5("span").withConfig({
  componentId: "sc-sf-1rkjxse-4"
})`${p => p.$_css4}`;
var _StyledH = _styled6("h2").withConfig({
  componentId: "sc-sf-1rkjxse-5"
})`${p => p.$_css5}`;
var _StyledSpan2 = _styled7("span").withConfig({
  componentId: "sc-sf-1rkjxse-6"
})({
  "color": "var(--text-secondary)"
});
var _StyledDiv5 = _styled8("div").withConfig({
  componentId: "sc-sf-1rkjxse-7"
})`${p => p.$_css6}`;
var _StyledDiv6 = _styled9("div").withConfig({
  componentId: "sc-sf-1rkjxse-8"
})`${p => p.$_css7}`;
var _StyledDiv7 = _styled10("div").withConfig({
  componentId: "sc-sf-1rkjxse-9"
})({
  "position": "relative",
  "height": "100%",
  "width": "100%"
});
var _StyledSection = _styled11("section").withConfig({
  componentId: "sc-sf-1rkjxse-10"
})`${p => p.$_css8}`;
var _StyledGrid = /*#__PURE__*/_styled12(Grid).withConfig({
  displayName: "intro-headline-b___StyledGrid",
  componentId: "sc-tw6j63-0"
})(["", ""], p => p.$_css9);